// The pieces every list of meetings is made of (COMX-FE-03): the card of a meeting, the compact
// line of a call, the status chip and the PV chip. The same components serve the chat timeline,
// the project and space tabs and the calendar, so a partner sees exactly what a member sees (what
// the API returns). Directional text is isolated: names with `<bdi>`, numbers and codes LTR.
import { useSession } from '@batiflow/client-core/react';
import { DateText, StatusChip, isolate } from '@batiflow/ui';
import {
  Button,
  __css,
  mergeClasses,
  tokens,
  type GriffelStyle,
} from '@fluentui/react-components';
import {
  Call16Regular,
  CallEnd16Regular,
  CallMissed16Regular,
  Document16Regular,
  Video16Regular,
  Video24Regular,
} from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { MeetingSummary } from './api';
import { openCallWindow } from './call-window';
import { durationParts, minutesPath } from './model';

/** A solid border on every side (Griffel takes longhand, logical properties only). */
function ring(color: string): GriffelStyle {
  const side = { Width: tokens.strokeWidthThin, Style: 'solid', Color: color } as const;
  return {
    borderBlockStartWidth: side.Width,
    borderBlockStartStyle: side.Style,
    borderBlockStartColor: side.Color,
    borderBlockEndWidth: side.Width,
    borderBlockEndStyle: side.Style,
    borderBlockEndColor: side.Color,
    borderInlineStartWidth: side.Width,
    borderInlineStartStyle: side.Style,
    borderInlineStartColor: side.Color,
    borderInlineEndWidth: side.Width,
    borderInlineEndStyle: side.Style,
    borderInlineEndColor: side.Color,
  };
}

const useStyles = __css({"card":{"mc9l5x":"f22iagw","Bt984gj":"f6jr5hl","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l","Eh141a":"fni485r","B7ck84d":"f1ewtqcl","Dbcxam":0,"rjzwhg":0,"Bblux5w":"fnllxr2","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","De3pzq":"fxugw4r","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f1o0qvyv","gggo0z":"fi15kbk","B834pou":"f1taw3pw","F1poyb":"foi3q75","Behc1zw":"f10zy6vx","uk9mye":"f1p2dij1","mbw9mh":"f1a566p","B4m11q7":"f32hy0g","rm1204":"fwyddsq","ijz38e":"f1ftuoky","tzxy30":"fgmlvap","Bwezv66":"fwl1lcr","Ctt3hk":"f1crbv9w"},"live":{"gggo0z":"fi15kbk","B834pou":"f1taw3pw","F1poyb":"fo0u7mh","Behc1zw":"f10zy6vx","uk9mye":"f1p2dij1","mbw9mh":"fh32zs","B4m11q7":"f32hy0g","rm1204":"fwyddsq","ijz38e":"fyvlgxw","tzxy30":"fgmlvap","Bwezv66":"fwl1lcr","Ctt3hk":"f9srtw2"},"tile":{"Bnnss6s":"fi64zpg","mc9l5x":"f22iagw","Bt984gj":"f122n59","Brf1p80":"f4d9j23","Bns2gvi":"ftrql5j","st59p4":"fwyv573","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","sj55zd":"f16muhyy","De3pzq":"f16xkysk"},"body":{"Bh6795r":"fqerorx","xawz":"f1name99","Bgto7am":"fuzgk9b","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9"},"titleRow":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1j0q4x9"},"title":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bg96gwp":"f1i3iumi","Bhrd7zp":"fl43uef","Btd35i7":"fjksvth"},"meta":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fy9rknc","Bg96gwp":"fwrc4pm","sj55zd":"fkfq4zb","Btd35i7":"fjksvth"},"actions":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f778t4r"},"ltr":{"fz3jsb":"f1vfkb3d","kgocjf":["f1r2v9sp","f1va4sj6"]},"pv":{"mc9l5x":"ftuwxu6","Bt984gj":"f122n59","i8kkvl":"f1ufnopg","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f1vjz9w0","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f44lkw9","gggo0z":"fi15kbk","B834pou":"f1taw3pw","F1poyb":"f1nxen24","Behc1zw":"f10zy6vx","uk9mye":"f1p2dij1","mbw9mh":"f1uccd92","B4m11q7":"f32hy0g","rm1204":"fwyddsq","ijz38e":"f32ghg3","tzxy30":"fgmlvap","Bwezv66":"fwl1lcr","Ctt3hk":"f129z43g","sj55zd":"fyind8e","De3pzq":"fxugw4r","Be2twd7":"fy9rknc","Bhrd7zp":"fl43uef","w71qe1":"f1iuv45f","Jwef8y":"f1knas48","Beohmeb":"f1u5sdlw","B486eqv":"f1u8i3ug","vu0emg":"fvi4nxw"},"callLine":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f47sw1k","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","Be2twd7":"fy9rknc","sj55zd":"fkfq4zb"},"missed":{"sj55zd":"f1hcrxcs"}});

/** Opens the page that writes the PV of a meeting (it asks for the template, then creates the draft). */
export function useWriteMinutes(): (meetingId: string, spaceId: string) => void {
  const navigate = useNavigate();
  return (meetingId, spaceId) => {
    void navigate({ to: '/minutes/new', search: { meetingId, spaceId } });
  };
}

/** "12 min" or "1 h 05": a number and its unit, kept left to right. */
export function DurationText({ seconds }: { seconds: number }) {
  const { t } = useTranslation('meetings');
  const styles = useStyles();
  const { hours, minutes } = durationParts(seconds);
  const text =
    hours > 0
      ? t('duration.hoursMinutes', { hours, minutes: String(minutes).padStart(2, '0') })
      : t('duration.minutes', { count: minutes });
  return <span className={styles.ltr}>{text}</span>;
}

/** The status of a meeting: always a word and an icon, never only a colour. */
export function MeetingStatusChip({ meeting }: { meeting: MeetingSummary }) {
  const { t } = useTranslation('meetings');
  if (meeting.status === 'live')
    return <StatusChip intent="success" icon={<Video16Regular />} label={t('status.live')} />;
  if (meeting.status === 'scheduled')
    return <StatusChip intent="informative" label={t('status.scheduled')} />;
  return <StatusChip intent="neutral" icon={<CallEnd16Regular />} label={t('status.ended')} />;
}

/** "PV-007" (or "PV" while the number is not yet given): a link to the minutes. */
export function PvChip({
  spaceId,
  minutes,
}: {
  spaceId: string;
  minutes: NonNullable<MeetingSummary['minutes']>;
}) {
  const { t } = useTranslation('meetings');
  const styles = useStyles();
  const navigate = useNavigate();
  const href = minutesPath(spaceId, minutes.id);
  const label = minutes.number ?? t('minutes.draftNumber');
  return (
    <a
      className={styles.pv}
      href={href}
      aria-label={t('minutes.open', { number: label })}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        void navigate({
          to: '/minutes/$spaceId/$minutesId',
          params: { spaceId, minutesId: minutes.id },
        });
      }}
    >
      <Document16Regular aria-hidden="true" />
      <span className={styles.ltr}>{label}</span>
    </a>
  );
}

export interface MeetingCardProps {
  meeting: MeetingSummary;
  /** Show where the meeting belongs (space and project): lists that mix several spaces. */
  showPlace?: boolean;
}

/**
 * A meeting as a card: title and status, who hosts it, when, who is in, the "Rejoindre" button
 * while it is open, and the PV once there is one. Readable on a phone (the content wraps).
 */
export function MeetingCard({ meeting, showPlace = false }: MeetingCardProps) {
  const { t } = useTranslation('meetings');
  const styles = useStyles();
  const navigate = useNavigate();
  const writeMinutes = useWriteMinutes();
  const userId = useSession((state) => state.me?.user.id ?? null);
  const joinable = meeting.status !== 'ended';
  const when =
    meeting.status === 'live' && meeting.startedAt ? (
      <>
        {t('card.startedAt')} <DateText value={meeting.startedAt} variant="dateTime" />
      </>
    ) : meeting.status === 'scheduled' && meeting.scheduledAt ? (
      <>
        {t('card.scheduledFor')} <DateText value={meeting.scheduledAt} variant="dateTime" />
      </>
    ) : meeting.status === 'ended' && (meeting.endedAt ?? meeting.startedAt) ? (
      <>
        <DateText value={(meeting.endedAt ?? meeting.startedAt) as string} variant="dateTime" />
        {meeting.durationSeconds !== null ? (
          <>
            {' · '}
            <DurationText seconds={meeting.durationSeconds} />
          </>
        ) : null}
      </>
    ) : null;
  const names = meeting.participantNames.map((name) => isolate(name)).join(t('card.separator'));
  const more = meeting.participantCount - meeting.participantNames.length;
  const who =
    meeting.participantNames.length === 0
      ? null
      : more > 0
        ? t('card.withMore', { names, count: more })
        : t('card.with', { names });
  const place = [meeting.spaceName, meeting.projectName].filter(
    (value, index, all): value is string => value !== null && all.indexOf(value) === index,
  );
  // Writing the minutes is offered to the organiser of an ended meeting (the API decides).
  const canWrite =
    meeting.kind === 'meeting' &&
    meeting.status === 'ended' &&
    meeting.minutes === null &&
    meeting.spaceId !== null &&
    meeting.createdBy === userId;

  return (
    <article
      className={mergeClasses(styles.card, meeting.status === 'live' && styles.live)}
      aria-label={meeting.title}
    >
      <span className={styles.tile} aria-hidden="true">
        <Video24Regular />
      </span>
      <div className={styles.body}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>
            <bdi>{meeting.title}</bdi>
          </h3>
          <MeetingStatusChip meeting={meeting} />
        </div>
        <p className={styles.meta}>
          {t('card.host', { name: isolate(meeting.createdByName) })}
          {when ? <> · {when}</> : null}
        </p>
        {showPlace && place.length > 0 ? (
          <p className={styles.meta}>{place.map((name) => isolate(name)).join(' · ')}</p>
        ) : null}
        {who ? <p className={styles.meta}>{who}</p> : null}
        {meeting.status === 'live' ? (
          <p className={styles.meta}>{t('card.present', { count: meeting.presentCount })}</p>
        ) : null}
      </div>
      <div className={styles.actions}>
        {joinable ? (
          <Button
            appearance={meeting.status === 'live' ? 'primary' : 'secondary'}
            icon={<Video16Regular />}
            aria-label={t('card.joinLabel', { title: meeting.title })}
            onClick={() => {
              void navigate({ to: '/meetings/$meetingId', params: { meetingId: meeting.id } });
            }}
          >
            {t('join.action')}
          </Button>
        ) : null}
        {meeting.minutes !== null && meeting.spaceId !== null ? (
          <PvChip spaceId={meeting.spaceId} minutes={meeting.minutes} />
        ) : null}
        {canWrite && meeting.spaceId !== null ? (
          <Button
            appearance="secondary"
            size="small"
            onClick={() => {
              writeMinutes(meeting.id, meeting.spaceId as string);
            }}
          >
            {t('minutes.write')}
          </Button>
        ) : null}
      </div>
    </article>
  );
}

/**
 * A call in a conversation: one quiet line ("Appel · 12 min", "Appel manqué"). While the call is
 * live, "Rejoindre" opens it in the call window (a person who missed the ring can still join).
 */
export function CallLine({ meeting }: { meeting: MeetingSummary }) {
  const { t } = useTranslation('meetings');
  const styles = useStyles();
  const navigate = useNavigate();
  const live = meeting.status === 'live';
  return (
    <p className={mergeClasses(styles.callLine, meeting.missed && styles.missed)} role="note">
      {meeting.missed ? (
        <CallMissed16Regular aria-hidden="true" />
      ) : (
        <CallEnd16Regular aria-hidden="true" />
      )}
      <span>
        {meeting.missed ? (
          <>
            {t('call.missed')} · <bdi>{meeting.createdByName}</bdi>
          </>
        ) : live ? (
          t('call.live')
        ) : (
          <>
            {t('call.line')}
            {meeting.durationSeconds !== null ? (
              <>
                {' · '}
                <DurationText seconds={meeting.durationSeconds} />
              </>
            ) : null}
          </>
        )}
      </span>
      {meeting.startedAt ? <DateText value={meeting.startedAt} variant="dateTime" /> : null}
      {live ? (
        <Button
          appearance="primary"
          size="small"
          icon={<Call16Regular />}
          aria-label={t('call.joinLabel')}
          onClick={() => {
            if (!openCallWindow(meeting.id)) {
              void navigate({ to: '/calls/$meetingId', params: { meetingId: meeting.id } });
            }
          }}
        >
          {t('join.action')}
        </Button>
      ) : null}
    </p>
  );
}


import "/opt/batiflow/app/apps/web/src/features/meetings/meeting-parts.griffel.css";